import React, { useState, useEffect, useCallback } from "react";
import { BUILD_ID } from "../../version";

// How often to re-check for a newer deployed build while a session stays open.
const CHECK_INTERVAL_MS = 5 * 60 * 1000;

/**
 * Detects when a newer build has been deployed while the user still has an old
 * bundle loaded, and offers a one-click refresh.
 *
 * The running bundle knows its own BUILD_ID (baked in at build time). It polls
 * the static /version.json (which nginx serves with no-cache, cache-busted via
 * a query param here) — when the server's buildId differs, a new deploy has
 * landed, so we surface a non-blocking banner. No native alert/confirm (rule).
 */
const UpdateBanner = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);

  const check = useCallback(async () => {
    try {
      const res = await fetch(`/version.json?ts=${Date.now()}`, { cache: "no-store" });
      if (!res.ok) return;
      const data = await res.json().catch(() => null);
      if (data && data.buildId && data.buildId !== BUILD_ID) {
        setUpdateAvailable(true);
      }
    } catch (e) {
      /* best-effort — ignore network hiccups, try again next tick */
    }
  }, []);

  useEffect(() => {
    check();
    const id = setInterval(check, CHECK_INTERVAL_MS);
    // Re-check when the user returns to the tab — catches deploys that landed
    // while the tab was in the background.
    const onFocus = () => check();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(id);
      window.removeEventListener("focus", onFocus);
    };
  }, [check]);

  if (!updateAvailable) return null;

  return (
    <div className="bg-blue-600 text-white text-sm px-4 py-2 flex items-center justify-center gap-3 shrink-0">
      <span>A new version is available.</span>
      <button
        onClick={() => window.location.reload()}
        className="inline-flex items-center rounded bg-white/20 hover:bg-white/30 px-2.5 py-1 font-medium transition-colors"
      >
        Refresh Now
      </button>
    </div>
  );
};

export default UpdateBanner;
